import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export const TYPE_SECTIONS = {
    swot: [['forces', 'Forces'], ['faiblesses', 'Faiblesses'], ['opportunites', 'Opportunités'], ['menaces', 'Menaces']],
    pestel: [['politique', 'Politique'], ['economique', 'Économique'], ['socioculturel', 'Socioculturel'], ['technologique', 'Technologique'], ['ecologique', 'Écologique'], ['legal', 'Légal']],
    bmc: [['partenaires', 'Partenaires clés'], ['activites', 'Activités clés'], ['ressources', 'Ressources clés'], ['proposition', 'Proposition de valeur'], ['relation', 'Relation client'], ['canaux', 'Canaux'], ['segments', 'Segments clients'], ['couts', 'Structure de coûts'], ['revenus', 'Sources de revenus']],
    cev: [['strategie', 'Stratégie'], ['finance', 'Finance'], ['marketing', 'Marketing'], ['rh', 'RH'], ['gouvernance', 'Gouvernance'], ['innovation', 'Innovation'], ['international', 'Développement international'], ['management', 'Management']],
};

const SECTION_COLORS = {
    forces: 'border-emerald-200 bg-emerald-50/40', faiblesses: 'border-rose-200 bg-rose-50/40',
    opportunites: 'border-sky-200 bg-sky-50/40', menaces: 'border-amber-200 bg-amber-50/40',
};

function SectionList({ sectionKey, label, items, onChange, testPrefix }) {
    const [draft, setDraft] = useState('');
    const add = () => {
        const v = draft.trim();
        if (!v) return;
        onChange([...items, v]);
        setDraft('');
    };
    return (
        <div className={`border rounded-lg p-3 ${SECTION_COLORS[sectionKey] || 'border-border'}`} data-testid={`${testPrefix}-section-${sectionKey}`}>
            <div className="text-xs uppercase tracking-wider font-medium text-foreground/70 mb-2">{label}</div>
            <ul className="space-y-1.5 mb-2">
                {items.map((it, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm bg-background/70 rounded px-2 py-1">
                        <span className="flex-1">{it}</span>
                        <button onClick={() => onChange(items.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive shrink-0 mt-0.5">
                            <X size={12} />
                        </button>
                    </li>
                ))}
            </ul>
            <div className="flex gap-1.5">
                <Input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Ajouter…" className="h-8 text-sm"
                    onKeyDown={e => e.key === 'Enter' && add()} data-testid={`${testPrefix}-input-${sectionKey}`} />
                <button onClick={add} className="h-8 w-8 shrink-0 rounded-md border border-border flex items-center justify-center hover:bg-surface-muted" data-testid={`${testPrefix}-add-${sectionKey}`}>
                    <Plus size={14} />
                </button>
            </div>
        </div>
    );
}

export function SectionsEditor({ type, value, onChange, testPrefix }) {
    const sections = TYPE_SECTIONS[type] || [];
    const data = value?.sections || {};
    const cols = type === 'swot' ? 'sm:grid-cols-2' : type === 'pestel' ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-3';
    return (
        <div className={`grid gap-3 ${cols}`}>
            {sections.map(([k, label]) => (
                <SectionList key={k} sectionKey={k} label={label} items={data[k] || []}
                    onChange={items => onChange({ sections: { ...data, [k]: items } })} testPrefix={testPrefix} />
            ))}
        </div>
    );
}

export function ScoringEditor({ value, onChange, testPrefix }) {
    const criteres = value?.criteres || [];
    const total = criteres.reduce((s, c) => s + (Number(c.note) || 0), 0);
    const maxTotal = criteres.reduce((s, c) => s + (Number(c.max) || 10), 0);
    const level = total >= 80 ? 'Projet mature' : total >= 60 ? 'Projet prometteur' : total >= 40 ? 'Projet à consolider' : 'Projet en émergence';
    const setCritere = (idx, patch) => onChange({ criteres: criteres.map((c, i) => i === idx ? { ...c, ...patch } : c) });
    return (
        <div className="space-y-3">
            <div className="flex items-baseline gap-3 border border-border rounded-lg p-4 bg-surface-muted/50">
                <div className="font-heading text-4xl text-primary" data-testid={`${testPrefix}-total`}>{total}<span className="text-lg text-muted-foreground">/{maxTotal}</span></div>
                <div className="text-sm font-medium">{level}</div>
            </div>
            <div className="space-y-2">
                {criteres.map((c, idx) => (
                    <div key={idx} className="flex flex-wrap items-center gap-2 border border-border rounded-lg px-3 py-2" data-testid={`${testPrefix}-critere-${idx}`}>
                        <div className="text-sm font-medium w-48 shrink-0">{c.nom}</div>
                        <Input type="number" min={0} max={c.max || 10} value={c.note}
                            onChange={e => setCritere(idx, { note: Math.max(0, Math.min(c.max || 10, Number(e.target.value))) })}
                            className="w-16 h-8 text-center" data-testid={`${testPrefix}-note-${idx}`} />
                        <span className="text-xs text-muted-foreground">/ {c.max || 10}</span>
                        <Input value={c.commentaire || ''} onChange={e => setCritere(idx, { commentaire: e.target.value })}
                            placeholder="Commentaire" className="flex-1 min-w-[180px] h-8 text-sm" />
                    </div>
                ))}
            </div>
        </div>
    );
}

export function TextEditor({ value, onChange, hint, testPrefix }) {
    return (
        <Textarea rows={12} value={value?.text || ''} placeholder={hint}
            onChange={e => onChange({ text: e.target.value })} className="text-sm leading-relaxed" data-testid={`${testPrefix}-text`} />
    );
}

export function LivrableEditor({ type, value, onChange, hint, testPrefix }) {
    if (TYPE_SECTIONS[type]) return <SectionsEditor type={type} value={value} onChange={onChange} testPrefix={testPrefix} />;
    if (type === 'scoring') return <ScoringEditor value={value} onChange={onChange} testPrefix={testPrefix} />;
    return <TextEditor value={value} onChange={onChange} hint={hint} testPrefix={testPrefix} />;
}
